import * as THREE from 'three';
import { fullGeometryOf } from './modeVisibility';

export type OwnedResource = THREE.BufferGeometry | THREE.Material | THREE.Texture | THREE.Texture['source'];
interface Entry { resources: Set<OwnedResource>; refs: number; used: number; pending: number; forget: () => void }
interface Lease { resources: Set<OwnedResource>; entries: Set<Entry> }

export function resourcesOf(root: THREE.Object3D): Set<OwnedResource> {
  const resources = new Set<OwnedResource>();
  root.traverse(object => {
    const mesh = object as THREE.Mesh;
    if (mesh.geometry) { resources.add(mesh.geometry); resources.add(fullGeometryOf(mesh)); }
    for (const material of Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : []) {
      resources.add(material);
      for (const value of Object.values(material)) if (value instanceof THREE.Texture) {
        resources.add(value); resources.add(value.source);
      }
    }
  });
  return resources;
}

export class ResourcePool {
  private entries = new Map<object, Entry>();
  private owners = new Map<OwnedResource, Set<Entry>>();
  private disposedResources = new WeakSet<object>();
  private closedImages = new WeakSet<object>();
  private leases = new Set<Lease>();
  private clock = 0;
  private disposed = false;
  constructor(readonly budget = 32 * 1024 * 1024) {}

  register(key: object, resources: Iterable<OwnedResource>, forget = () => {}, pending = 0): void {
    if (this.disposed) { this.disposeLoose(resources); return; }
    let entry = this.entries.get(key);
    if (!entry) { entry = { resources: new Set(), refs: 0, used: ++this.clock, pending, forget }; this.entries.set(key, entry); }
    entry.pending = pending;
    for (const resource of resources) {
      entry.resources.add(resource);
      let owners = this.owners.get(resource);
      if (!owners) { owners = new Set(); this.owners.set(resource, owners); }
      owners.add(entry);
    }
  }

  pin(key: object): () => void {
    const entry = this.entries.get(key);
    return this.pinEntries(entry ? new Set([entry]) : new Set());
  }

  retain(resources: Iterable<OwnedResource>): () => void {
    const requested = new Set(resources);
    // A sampler borrower follows that sampler's current Source after an atomic
    // upgrade. A private preview sampler keeps its own, still-legacy Source.
    for (const resource of requested) if (resource instanceof THREE.Texture) requested.delete(resource.source);
    const lease: Lease = { resources: requested, entries: new Set() };
    this.leases.add(lease); this.refreshLease(lease);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      for (const entry of lease.entries) { entry.refs--; entry.used = ++this.clock; }
      this.leases.delete(lease); lease.entries.clear(); lease.resources.clear();
      this.trim();
    };
  }

  private refreshLease(lease: Lease): void {
    const entries = new Set<Entry>();
    for (const resource of lease.resources) {
      for (const entry of this.owners.get(resource) ?? []) entries.add(entry);
      if (resource instanceof THREE.Texture) for (const entry of this.owners.get(resource.source) ?? []) entries.add(entry);
    }
    for (const entry of lease.entries) if (!entries.has(entry)) entry.refs--;
    for (const entry of entries) if (!lease.entries.has(entry)) entry.refs++;
    lease.entries = entries;
  }

  /** Called after every canonical sampler has switched to the ready compressed
   * Source. Keep a separate retired owner only for genuine legacy borrowers. */
  replaceSource(key: object, previous: THREE.Texture['source'], next: THREE.Texture['source']): void {
    const entry = this.entries.get(key);
    if (!entry || previous === next) return;
    this.register(previous, [previous]);
    this.register(key, [next]);
    entry.resources.delete(previous);
    this.owners.get(previous)?.delete(entry);
    for (const lease of this.leases) this.refreshLease(lease);
  }

  private pinEntries(entries: Set<Entry>): () => void {
    for (const entry of entries) { entry.refs++; entry.used = ++this.clock; }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      for (const entry of entries) { entry.refs--; entry.used = ++this.clock; }
      entries.clear();
    };
  }

  has(resource: OwnedResource): boolean { return this.owners.has(resource); }
  /** Whether a lease holds what is registered under `key`: something on screen draws it. */
  retained(key: object): boolean { return (this.entries.get(key)?.refs ?? 0) > 0; }

  trim(): void {
    const unused = [...this.entries].filter(([, entry]) => !entry.refs && !entry.pending).sort((a, b) => a[1].used - b[1].used);
    for (const [key] of unused) {
      if (this.charge(false) <= this.budget && this.budget > 0) break;
      this.remove(key);
    }
  }

  remove(key: object): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    this.entries.delete(key); entry.forget(); entry.forget = () => {};
    const released: OwnedResource[] = [];
    for (const resource of entry.resources) {
      const owners = this.owners.get(resource)!;
      owners.delete(entry);
      if (!owners.size) { this.owners.delete(resource); released.push(resource); }
    }
    this.disposeLoose(released);
    entry.resources.clear();
  }

  disposeLoose(resources: Iterable<OwnedResource>): void {
    for (const resource of resources) {
      if (this.owners.has(resource) || this.disposedResources.has(resource)) continue;
      this.disposedResources.add(resource);
      if (resource instanceof THREE.Source) this.closeImage(resource.data);
      else resource.dispose();
    }
  }

  closeImage(image: unknown): void {
    if (!image || typeof image !== 'object' || this.closedImages.has(image)) return;
    for (const resource of this.owners.keys()) if (resource instanceof THREE.Source && resource.data === image) return;
    if ('close' in image && typeof image.close === 'function') { image.close(); this.closedImages.add(image); }
  }

  private charge(live: boolean): number {
    const resources = [...this.owners].filter(([, owners]) => [...owners].some(entry => entry.refs > 0) === live).map(([resource]) => resource);
    return resourceBytes(resources).charge;
  }

  stats() {
    const all = [...this.owners.keys()], bytes = resourceBytes(all);
    return {
      geometries: all.filter(r => r instanceof THREE.BufferGeometry).length,
      materials: all.filter(r => r instanceof THREE.Material).length,
      textureSources: all.filter(r => r instanceof THREE.Source).length,
      samplers: all.filter(r => r instanceof THREE.Texture).length,
      geometryBytes: bytes.geometry, decodedImageBytesEstimate: bytes.images, compressedMipBytes: bytes.compressed,
      textureBytesEstimate: bytes.textures, textureFormats: bytes.formats,
      liveBytes: this.charge(true), warmBytes: this.charge(false), warmBudgetBytes: this.budget,
      pendingLoads: [...this.entries.values()].filter(e => e.pending > 0).length,
      queuedLoads: [...this.entries.values()].filter(e => e.pending < 0).length,
      pendingBytes: [...this.entries.values()].reduce((sum, e) => sum + Math.max(0, e.pending), 0),
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const key of [...this.entries.keys()]) this.remove(key);
    for (const lease of this.leases) { lease.resources.clear(); lease.entries.clear(); }
    this.leases.clear();
  }
}

function resourceBytes(resources: OwnedResource[]) {
  const buffers = new Set<ArrayBufferLike>(), images = new Set<object>(), mipBuffers = new Set<ArrayBufferLike>();
  const sourceTextures = new Map<THREE.Texture['source'], THREE.Texture>();
  const formats: Record<string, { sources: number; bytes: number }> = {};
  for (const resource of resources) if (resource instanceof THREE.Texture) sourceTextures.set(resource.source, resource);
  let imageBytes = 0, textureBytes = 0, metadata = 0;
  for (const resource of resources) {
    if (resource instanceof THREE.BufferGeometry) {
      const attributes = [...Object.values(resource.attributes), ...Object.values(resource.morphAttributes).flat(), resource.index] as Array<THREE.BufferAttribute | THREE.InterleavedBufferAttribute | null>;
      for (const attribute of attributes) if (attribute) buffers.add(attribute instanceof THREE.InterleavedBufferAttribute ? attribute.data.array.buffer : attribute.array.buffer);
    } else if (resource instanceof THREE.Source) {
      const image = resource.data as { width?: number; height?: number; data?: ArrayBufferView } | undefined;
      if (image && typeof image === 'object' && !images.has(image)) {
        images.add(image);
        const compressed = sourceTextures.get(resource);
        if (compressed && 'isCompressedTexture' in compressed) {
          for (const mip of compressed.mipmaps) if ('data' in mip) mipBuffers.add(mip.data.buffer);
        } else imageBytes += image.data?.byteLength ?? (image.width && image.height ? image.width * image.height * 4 : 0);
      }
    } else if (resource instanceof THREE.Texture) {
      metadata += 1024;
    } else metadata += 1024;
  }
  for (const texture of sourceTextures.values()) {
    const mipBytes = texture.mipmaps.reduce((sum, mip) => sum + ('data' in mip ? mip.data.byteLength : 0), 0);
    const image = texture.image as { width?: number; height?: number } | undefined;
    const bytes = mipBytes || (image?.width && image?.height ? image.width * image.height * 4 * (texture.generateMipmaps ? 4 / 3 : 1) : 0);
    textureBytes += bytes;
    const format = 'isCompressedTexture' in texture ? String(texture.format) : 'RGBA8 estimate';
    const group = formats[format] ??= { sources: 0, bytes: 0 }; group.sources++; group.bytes += bytes;
  }
  const geometry = [...buffers].reduce((sum, buffer) => sum + buffer.byteLength, 0);
  const compressed = [...mipBuffers].reduce((sum, buffer) => sum + buffer.byteLength, 0);
  return { geometry, images: imageBytes, compressed, textures: textureBytes, formats, charge: geometry + imageBytes + compressed + metadata };
}
